import {Group} from '@visx/group';
import {scaleBand} from '@visx/scale';
import {BarGroup} from '@visx/shape';
import React from 'react';

import {VisxAxisScale, VisxScaleType} from 'ui-library/charting/internals/VisxUtilityTypes';

import {BAR_PADDING_BETWEEN_BARS} from './BarChartConstants';
import {BarChartContentProps, BarChartScales} from './BarChartTypes';
import BarGroupBar from './BarGroupBar';

export function getGroupedBarChartYExtent(
  yValues: Array<number>,
  errorValues: Array<number> | null
): [number, number] {
  const lowerBoundYValues = yValues.map((yValue, i) => yValue - (errorValues == null ? 0 : errorValues[i]));
  const upperBoundYValues = yValues.map((yValue, i) => yValue + (errorValues == null ? 0 : errorValues[i]));

  return [Math.min(...lowerBoundYValues), Math.max(...upperBoundYValues)];
}

export function getSecondaryXScale(
  x0Scale: BarChartScales['xScale'],
  seriesUniqueValues: BarChartContentProps['seriesUniqueValues']
): VisxAxisScale<VisxScaleType.BAND> {
  return scaleBand({
    domain: seriesUniqueValues,
    range: [0, x0Scale.bandwidth()],
  }).paddingInner(seriesUniqueValues.length > 1 ? BAR_PADDING_BETWEEN_BARS : 0);
}

export default function GroupedBarChartContent({
  accessors: {errorValueAccessor, rowIdAccessor, seriesAccessor, xAccessor, yAccessor},
  barGroupDicts,
  barGroupRowMaps,
  chartWidth,
  chartHeight,
  handleOnHoverBar,
  highlightedSeries,
  hoveredRow,
  isSeriesEnabled,
  seriesUniqueValues,
  scales: {colorScale, xScale: x0Scale, yScale},
}: BarChartContentProps): JSX.Element {
  const x1Scale = getSecondaryXScale(x0Scale, seriesUniqueValues);

  return (
    <BarGroup
      color={colorScale}
      data={barGroupDicts}
      keys={seriesUniqueValues}
      height={chartHeight}
      width={chartWidth}
      x0={xAccessor}
      x0Scale={x0Scale}
      x1Scale={x1Scale}
      // @ts-expect-error BarGroup doesn't support scales that return undefined type-wise, but our scale
      // does return undefined when we try to put data into it that doesn't exist (e.g. in cases where
      // the data is sparse, meaning we don't have every bar for every group). We want to return undefined
      // in that case, but that will make BarGroup just draw a full height bar. Instead we just handle this
      // case by not rendering a BarChartBar when we get undefined values in bars. This all means we have
      // to incorrectly use this type.
      yScale={yScale}
    >
      {(barGroups) =>
        barGroups.map(({bars, x0, index: barGroupIndex}) => (
          <Group key={`bar-group-${barGroupIndex}-${x0}`} left={x0}>
            {bars.map((barProps, barIndex) => {
              if (barProps.value == null) {
                return null;
              }

              const barRow = barGroupRowMaps[barGroupIndex].rowMap[barProps.key];
              return (
                barProps.value && (
                  <BarGroupBar
                    {...barProps}
                    dataFrameRow={barRow}
                    errorValueAccessor={errorValueAccessor}
                    isFaded={
                      isSeriesEnabled
                        ? highlightedSeries != null && seriesAccessor(barRow) !== highlightedSeries
                        : hoveredRow != null && rowIdAccessor(barRow) !== rowIdAccessor(hoveredRow)
                    }
                    key={`bar-group-${barGroupIndex}-${x0}-bar-${barIndex}`}
                    onHover={(hoverInfo) => {
                      handleOnHoverBar(hoverInfo != null ? {...hoverInfo, barGroupInfo: {x: x0}} : hoverInfo);
                    }}
                    stepWidth={x1Scale.step()}
                    yScale={yScale}
                    yAccessor={yAccessor}
                  />
                )
              );
            })}
          </Group>
        ))
      }
    </BarGroup>
  );
}
